import { create } from 'zustand'
import { TableDataJSON } from '../../src/types/table-json'
import type { 
  CalculatorConfig, 
  CalculatorControl, 
  ControlValues,
  ControlId,
  CalculatorId,
  CalculatorTypes
} from './calculator-config'
import { DataTableInputMode } from './calculator-config'

// Separate state interface for clarity
interface State<T extends keyof CalculatorTypes> {
  data: TableDataJSON | null
  controlValues: ControlValues
  config: CalculatorConfig<T> | null
  visualizationData: ReturnType<NonNullable<CalculatorConfig<T>['getVisualizationData']>> | null
}

/**
 * Compute visualization data based on current state
 */
function computeVisualizationData<T extends keyof CalculatorTypes>(
  config: CalculatorConfig<T> | null,
  data: TableDataJSON | null,
  controlValues: ControlValues
) {
  // Early return if no config
  if (!config) return null;

  try {
    // Skip data check for calculators that don't require data
    if (config.dataTableInputMode === DataTableInputMode.None) {
      return config.getVisualizationData({ data: new TableDataJSON([]), controls: controlValues });
    }

    // For calculators that require data, ensure data is available
    if (!data) return null;

    // Ensure data has valid columns
    if (!data.columns || data.columns.length === 0) return null;

    // Check if required control values are present
    const hasRequiredControls = config.controls.tabs
      .flatMap(tab => tab.controls)
      .filter(control => control.required)
      .every(control => {
        const value = controlValues[control.id];
        return value !== undefined && value !== '';
      });

    if (!hasRequiredControls) return null;

    return config.getVisualizationData({ data, controls: controlValues });
  } catch (error) {
    console.error('Error computing visualization data:', error);
    return null;
  }
}

/**
 * Create a calculator store for a specific calculator type
 */
export function createCalculatorStore<T extends keyof CalculatorTypes>() {
  return create<State<T> & {
    setData: (data: TableDataJSON | null) => void
    setConfig: (config: CalculatorConfig<T> | null) => void
    setControlValue: (id: string, value: string | string[]) => void
    resetControlValues: () => void
  }>((set, get) => ({
    data: null,
    controlValues: {} as ControlValues,
    config: null,
    visualizationData: null,

    setData: (data) => {
      const state = get()
      // Don't update if the data is the same
      if (!data || !state.data) {
        set({ 
          data,
          visualizationData: computeVisualizationData(state.config, data, state.controlValues)
        })
        return
      }

      // Check if data has changed
      if (data.columns.length !== state.data.columns.length) {
        set({ 
          data,
          visualizationData: computeVisualizationData(state.config, data, state.controlValues)
        })
        return
      }

      // Check if any column has changed
      const hasChanged = data.columns.some((col, i) => {
        if (i >= state.data!.columns.length) return true
        const stateCol = state.data!.columns[i]
        if (!stateCol) return true
        return (
          col.id !== stateCol.id ||
          col.name !== stateCol.name ||
          col.values.length !== stateCol.values.length ||
          !col.values.every((v, j) => v === stateCol.values[j])
        )
      })

      if (hasChanged) {
        set({ 
          data,
          visualizationData: computeVisualizationData(state.config, data, state.controlValues)
        })
      }
    },

    setConfig: (config) => {
      const state = get()
      
      // Initialize control values from config if provided
      let controlValues = { ...state.controlValues }
      
      if (config) {
        if (config.dataTableInputMode === DataTableInputMode.None) {
          // For calculators that don't require data, use empty data for initialization
          const initialConfig = config.getInitialConfig(new TableDataJSON([]))
          
          // Merge with any explicitly provided initial values
          controlValues = {
            ...initialConfig,
            ...config.initialControlValues
          }
        } else if (state.data) {
          // For calculators that require data, ensure data is available
          const initialConfig = config.getInitialConfig(state.data)
          
          // Merge with any explicitly provided initial values
          controlValues = {
            ...initialConfig,
            ...config.initialControlValues
          }
        }
      }
      
      set({ 
        config,
        controlValues,
        visualizationData: computeVisualizationData(config, state.data, controlValues)
      })
    },

    setControlValue: (id, value) => {
      const state = get()
      const controlValues = { ...state.controlValues }
      
      // Handle array values (for multi-select controls)
      if (Array.isArray(value)) {
        controlValues[id] = value
      } else {
        controlValues[id] = value
      }
      
      set({ 
        controlValues,
        visualizationData: computeVisualizationData(state.config, state.data, controlValues)
      })
    },

    resetControlValues: () => {
      const state = get()
      
      if (!state.config || !state.data) {
        set({ controlValues: {} as ControlValues, visualizationData: null })
        return
      }
      
      // Get initial control values from config based on current data
      const initialConfig = state.config.getInitialConfig(state.data)
      
      // Use only the initial config values without merging with initialControlValues
      // This ensures we use values appropriate for the current data structure
      const controlValues = {
        ...initialConfig
      }
      
      set({ 
        controlValues,
        visualizationData: computeVisualizationData(state.config, state.data, controlValues)
      })
    }
  }))
} 